import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaStar, FaTimes, FaUserCircle, FaCheck, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import api from "../api/Api"; 

const THEME = {
    background: "#FDF8F5",       
    surface: "#FFFFFF",          
    titleText: "#1A1A1A",        
    mutedText: "#6B7280",        
    primaryOrange: "#F26E22",    
    iconGradient: "linear-gradient(135deg, #FF9B61 0%, #F26E22 100%)",
};

// Mapped directly to client's 8 Categories
const POSITIVE_TAGS = [
    "Arrived on Time",      // Cat 1: Punctuality
    "Safe & Smooth",        // Cat 2: Driving Skills
    "Polite & Professional",// Cat 3: Behavior
    "Clean Car & AC",       // Cat 4: Vehicle Condition
    "Optimal Route",        // Cat 5: Route Knowledge
    "Fair Price"            // Cat 6: Payment
];

const NEGATIVE_TAGS = [
    "Driver Late",          // Cat 1: Punctuality
    "Rash Driving",         // Cat 2: Driving Skills
    "Rude Behavior",        // Cat 3: Behavior
    "Unclean / Smelly",     // Cat 4: Vehicle Condition
    "Bad Navigation",       // Cat 5: Route Knowledge
    "Overcharged",          // Cat 6: Payment
    "Safety Concerns"       // Cat 8: Issues
];

export default function FeedbackPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const [rating, setRating] = useState(0);
    const [selectedTags, setSelectedTags] = useState([]);
    const [comment, setComment] = useState("");
    const [recommend, setRecommend] = useState(true); // Cat 7: Overall Satisfaction
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const bookingInfo = location.state || {
        driverName: "Assigned Driver",
        carModel: "Assigned Cab",
        tripId: "Unknown-ID",
        driverId: null
    };

    const handleStarClick = (index) => {
        setRating(index);
        setSelectedTags([]); 
        // Auto-set recommend based on rating
        setRecommend(index >= 4); 
    };

    const toggleTag = (tag) => {
        setSelectedTags((prev) => 
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    const handleSubmit = async () => {
        if (rating === 0) return alert("Please select a rating first.");
        
        setIsSubmitting(true);

        const feedbackData = {
            Enqid: bookingInfo.enqId,
            tripId: bookingInfo.tripId,
            driverId: bookingInfo.driverId,
            driverName: bookingInfo.driverName,
            rating,
            tags: selectedTags,
            recommend, 
            comment,
        };

        try {
            const token = localStorage.getItem("customerToken");
            const response = await api.post("/feedback/add", feedbackData, {
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            if (response.data.success) {
                setSubmitted(true);
                setTimeout(() => navigate("/dashboard"), 2000);
            } else {
                alert("Failed to submit feedback. Please try again.");
            }
        } catch (error) {
            console.error("Error:", error);
            alert("Network error. Could not submit feedback.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const activeTagsList = rating >= 4 ? POSITIVE_TAGS : NEGATIVE_TAGS;

    if (submitted) {
        return (
            <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: THEME.background }}>
                <div className="mb-4 text-white d-flex align-items-center justify-content-center animate-bounce shadow-lg" style={{ width: 80, height: 80, borderRadius: "50%", background: THEME.iconGradient }}>
                    <FaCheck size={40} />
                </div>
                <h3 className="fw-bold text-dark">Thank You!</h3>
                <p className="text-muted">Your feedback helps us improve.</p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .native-chip {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1.5px solid transparent;
                }
                .native-chip.unselected {
                    background-color: #FFFFFF;
                    border-color: #E5E7EB;
                    color: ${THEME.mutedText};
                }
                .native-chip.selected {
                    background: ${THEME.iconGradient};
                    color: #FFFFFF;
                    transform: scale(1.05);
                    box-shadow: 0 4px 12px rgba(242, 110, 34, 0.25);
                }
                .recommend-btn { transition: all 0.2s ease; border: 2px solid transparent; }
                .recommend-btn.active-yes { background-color: #dcfce7; color: #166534; border-color: #bbf7d0; }
                .recommend-btn.active-no { background-color: #fee2e2; color: #991b1b; border-color: #fecaca; }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes bounce { 0% { transform: scale(0); } 50% { transform: scale(1.15); } 100% { transform: scale(1); } }
            `}</style>

            <div className="d-flex flex-column min-vh-100 w-100" style={{ backgroundColor: THEME.background, fontFamily: "'Inter', sans-serif" }}>
                
                <div className="pt-4 px-3 pb-2 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0" style={{ color: THEME.titleText }}>Trip Feedback</h5>
                    <button onClick={() => navigate("/dashboard")} className="btn btn-link p-1 text-dark border-0">
                        <FaTimes size={24} color={THEME.mutedText} />
                    </button>
                </div>

                <div className="flex-grow-1 px-4 pt-3 pb-5 d-flex flex-column align-items-center text-center">
                    
                    <div className="mb-4">
                        <div className="bg-white rounded-circle d-inline-flex p-1 mb-3 shadow-sm" style={{ border: `2px solid ${THEME.primaryOrange}` }}>
                            <FaUserCircle size={65} color="#E5E7EB" />
                        </div>
                        <h4 className="fw-bold mb-1" style={{ color: THEME.titleText }}>{bookingInfo.driverName}</h4>
                        <p className="mb-0 fw-medium" style={{ color: THEME.mutedText, fontSize: "0.9rem" }}>{bookingInfo.carModel}</p>
                    </div>

                    <h6 className="fw-bold mb-3" style={{ color: THEME.titleText }}>How was your ride?</h6>
                    <div className="d-flex justify-content-center gap-2 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                                key={star}
                                size={38}
                                onClick={() => handleStarClick(star)}
                                style={{
                                    cursor: "pointer",
                                    color: star <= rating ? THEME.primaryOrange : "#E5E7EB",
                                    transform: star <= rating ? "scale(1.1)" : "scale(1)",
                                    transition: "all 0.2s ease"
                                }}
                            />
                        ))}
                    </div>

                    {rating > 0 && (
                        <div className="animate-fade-in w-100">
                            <p className="fw-semibold mb-3" style={{ color: THEME.titleText, fontSize: "0.95rem" }}>
                                {rating >= 4 ? "What went well?" : "What went wrong?"}
                            </p>

                            <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                                {activeTagsList.map((tag) => {
                                    const isSelected = selectedTags.includes(tag);
                                    return (
                                        <button
                                            key={tag}
                                            onClick={() => toggleTag(tag)}
                                            className={`btn rounded-pill px-3 py-2 fw-semibold native-chip ${isSelected ? "selected" : "unselected"}`}
                                            style={{ fontSize: "0.85rem" }}
                                        >
                                            {tag}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Client Request: Category 7 - Recommend Driver */}
                            <div className="bg-white p-3 rounded-4 shadow-sm mb-4 w-100 d-flex justify-content-between align-items-center">
                                <span className="fw-semibold text-start" style={{ fontSize: "0.9rem", color: THEME.titleText }}>
                                    Recommend this driver?
                                </span>
                                <div className="d-flex gap-2">
                                    <button 
                                        onClick={() => setRecommend(true)}
                                        className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center recommend-btn ${recommend ? 'active-yes' : 'bg-light text-muted'}`}
                                        style={{ width: "40px", height: "40px" }}
                                    >
                                        <FaThumbsUp />
                                    </button>
                                    <button 
                                        onClick={() => setRecommend(false)}
                                        className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center recommend-btn ${!recommend ? 'active-no' : 'bg-light text-muted'}`}
                                        style={{ width: "40px", height: "40px" }}
                                    >
                                        <FaThumbsDown />
                                    </button>
                                </div>
                            </div>

                            <div className="text-start mb-4 w-100">
                                <textarea
                                    className="form-control rounded-4 p-3 shadow-sm"
                                    rows="3"
                                    placeholder="Any other remarks? (Optional)"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    style={{ 
                                        backgroundColor: THEME.surface, 
                                        border: "none", 
                                        fontSize: "0.9rem",
                                        resize: "none"
                                    }}
                                ></textarea>
                            </div>
                        </div>
                    )}
                </div>

                {rating > 0 && (
                    <div className="p-3 bg-white w-100 mt-auto animate-fade-in" style={{ boxShadow: "0 -4px 12px rgba(0,0,0,0.03)", borderTopLeftRadius: "24px", borderTopRightRadius: "24px" }}>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="btn w-100 py-3 rounded-pill fw-bold text-white"
                            style={{ 
                                background: THEME.iconGradient, 
                                fontSize: "1.05rem",
                                opacity: isSubmitting ? 0.7 : 1
                            }}
                        >
                            {isSubmitting ? "Submitting..." : "Submit Feedback"}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}